import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Card, CardContent, Button, CircularProgress } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { notificationAPI } from '../api';

const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

// Pending customer payment confirmations for standalone (due) orders.
// Lives as the "Alerts" sub-tab of the Orders dashboard.
const OrderPaymentAlerts = ({ businessId, onCountChange }) => {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionId, setActionId] = useState(null);

    const load = useCallback(async () => {
        if (!businessId) return;
        try {
            const res = await notificationAPI.list(businessId, { status: 'pending', channel: 'orders' });
            const rows = res.data || [];
            setNotifications(rows);
            if (onCountChange) onCountChange(rows.length);
        } catch (err) {
            console.error('Failed to load order payment alerts:', err);
        } finally {
            setLoading(false);
        }
    }, [businessId, onCountChange]);

    useEffect(() => { load(); }, [load]);

    const resolve = async (n, accept) => {
        setActionId(n.f_id);
        try {
            if (accept) await notificationAPI.accept(businessId, n.f_id);
            else await notificationAPI.reject(businessId, n.f_id);
        } catch (err) {
            console.error('Resolve failed:', err);
        } finally {
            await load();
            setActionId(null);
        }
    };

    if (loading) {
        return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={28} /></Box>;
    }

    if (notifications.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6, px: 2, lineHeight: 1.6 }}>
                No new payment updates.<br />
                When a customer taps “I've paid” on an order, it shows here for you to confirm.
            </Typography>
        );
    }

    return (
        <Box>
            {notifications.map(n => (
                <Card key={n.f_id} variant="outlined" sx={{ mb: 1.25, borderRadius: 2, borderLeft: '3px solid', borderLeftColor: 'success.main' }}>
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight={800} noWrap>{n.customer_name || 'Customer'}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                    {n.reference_label} · {n.f_payment_method || 'UPI'} · {new Date(n.f_inserted_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                                </Typography>
                                {n.f_message && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        “{n.f_message}”
                                    </Typography>
                                )}
                            </Box>
                            <Typography variant="subtitle1" fontWeight={800} color="success.main" sx={{ flexShrink: 0 }}>
                                {inr(n.f_amount)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1.25 }}>
                            <Button
                                fullWidth size="small" variant="contained" color="success"
                                disabled={actionId === n.f_id}
                                startIcon={<CheckCircle sx={{ fontSize: '1rem' }} />}
                                onClick={() => resolve(n, true)}
                                sx={{ textTransform: 'none', fontWeight: 700, minHeight: 40 }}
                            >
                                Confirm
                            </Button>
                            <Button
                                size="small" variant="outlined" color="inherit"
                                disabled={actionId === n.f_id}
                                onClick={() => resolve(n, false)}
                                sx={{ textTransform: 'none', minHeight: 40 }}
                            >
                                Dismiss
                            </Button>
                        </Box>
                    </CardContent>
                </Card>
            ))}
        </Box>
    );
};

export default OrderPaymentAlerts;
